import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { Device, Call } from '@twilio/voice-sdk';
import { getVoiceToken } from '@/services/webchat-service/webchat.service';

export type TwilioDeviceStatus =
  | 'idle'
  | 'initializing'
  | 'ready'
  | 'connecting'
  | 'connected'
  | 'error';

export const useTwilioDevice = () => {
  const [status, setStatus] = useState<TwilioDeviceStatus>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const conversationIdRef = useRef<number | null>(null);

  const destroyResources = useCallback(() => {
    if (callRef.current) {
      callRef.current.disconnect();
      callRef.current = null;
    }
    if (deviceRef.current) {
      deviceRef.current.destroy();
      deviceRef.current = null;
    }
    conversationIdRef.current = null;
    setIsMuted(false);
  }, []);

  const connect = useCallback(
    async (conversationId: number, conferenceName: string, extraParams?: Record<string, string>) => {
      try {
        setStatus('initializing');
        setError(null);

        const { token } = await getVoiceToken(conversationId);
        conversationIdRef.current = conversationId;

        const device = new Device(token, { closeProtection: true });
        deviceRef.current = device;

        device.on('tokenWillExpire', async () => {
          if (!conversationIdRef.current) return;
          try {
            const { token: newToken } = await getVoiceToken(conversationIdRef.current);
            device.updateToken(newToken);
          } catch {
            // Token refresh failed — call will eventually disconnect
          }
        });

        await device.register();
        setStatus('connecting');

        const call = await device.connect({
          params: { conference_name: conferenceName, ...extraParams },
        });
        callRef.current = call;

        call.on('accept', () => {
          setStatus('connected');
        });

        call.on('disconnect', () => {
          callRef.current = null;
          setStatus('idle');
        });

        call.on('error', (callError: { message: string }) => {
          setError(callError.message);
          setStatus('error');
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : t('webchat:voiceDevice.connectFailed');
        destroyResources();
        setError(message);
        setStatus('error');
      }
    },
    [destroyResources]
  );

  const disconnect = useCallback(() => {
    destroyResources();
    setStatus('idle');
  }, [destroyResources]);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    const newMuted = !callRef.current.isMuted();
    callRef.current.mute(newMuted);
    setIsMuted(newMuted);
  }, []);

  useEffect(() => {
    return () => {
      destroyResources();
    };
  }, [destroyResources]);

  return {
    status,
    isMuted,
    error,
    connect,
    disconnect,
    toggleMute,
  };
};
